import React, { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, Server, Database, Edit, Trash2, RefreshCw, CheckCircle2, XCircle, Loader2, AlertCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const ConnectionsPage = () => {
  const [activeTab, setActiveTab] = useState('tally');
  const [tallyConnections, setTallyConnections] = useState([]);
  const [dbConnections, setDbConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [editingConnection, setEditingConnection] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConnection, setDeletingConnection] = useState(null);

  const [tallyForm, setTallyForm] = useState({
    name: '', ip_url: '', port: 9000, company_name: ''
  });

  const [dbForm, setDbForm] = useState({
    name: '', db_type: 'mysql', host: '', port: 3306, database: '', username: '', password: ''
  });

  const dbTypes = [
    { value: 'mysql', label: 'MySQL', defaultPort: 3306 },
    { value: 'mariadb', label: 'MariaDB', defaultPort: 3306 },
    { value: 'mssql', label: 'MS SQL Server', defaultPort: 1433 }
  ];

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const [tallyRes, dbRes] = await Promise.all([
        apiService.getTallyConnections(),
        apiService.getDBConnections()
      ]);
      setTallyConnections(tallyRes.data);
      setDbConnections(dbRes.data);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, connection = null) => {
    setDialogType(type);
    setEditingConnection(connection);
    if (type === 'tally') {
      setTallyForm(connection || { name: '', ip_url: '', port: 9000, company_name: '' });
    } else {
      setDbForm(connection || { name: '', db_type: 'mysql', host: '', port: 3306, database: '', username: '', password: '' });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogType(null);
    setEditingConnection(null);
  };

  const handleSaveTally = async () => {
    if (!tallyForm.name || !tallyForm.ip_url) {
      toast.error('Name and IP/URL are required');
      return;
    }
    try {
      if (editingConnection) {
        await apiService.updateTallyConnection(editingConnection.id, tallyForm);
        toast.success('Connection updated');
      } else {
        await apiService.createTallyConnection(tallyForm);
        toast.success('Connection created');
      }
      loadConnections();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save connection');
    }
  };

  const handleSaveDB = async () => {
    if (!dbForm.name || !dbForm.host || !dbForm.database || !dbForm.username) {
      toast.error('Name, Host, Database, and Username are required');
      return;
    }
    try {
      if (editingConnection) {
        await apiService.updateDBConnection(editingConnection.id, dbForm);
        toast.success('Connection updated');
      } else {
        await apiService.createDBConnection(dbForm);
        toast.success('Connection created');
      }
      loadConnections();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save connection');
    }
  };

  const handleTestConnection = async (type, id) => {
    setTestingId(id);
    try {
      const result = type === 'tally' 
        ? await apiService.testTallyConnection(id)
        : await apiService.testDBConnection(id);
      
      if (result.data.status === 'connected') {
        toast.success(result.data.message);
      } else {
        toast.error(result.data.message);
      }
      loadConnections();
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const openDeleteDialog = (type, connection) => {
    setDeletingConnection({ type, ...connection });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingConnection) return;
    try {
      if (deletingConnection.type === 'tally') {
        await apiService.deleteTallyConnection(deletingConnection.id);
      } else {
        await apiService.deleteDBConnection(deletingConnection.id);
      }
      toast.success('Connection deleted');
      loadConnections();
    } catch (error) {
      toast.error('Failed to delete connection');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingConnection(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="badge-success"><CheckCircle2 className="h-3 w-3 mr-1" />Connected</Badge>;
      case 'failed':
        return <Badge className="badge-error"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge className="badge-neutral"><AlertCircle className="h-3 w-3 mr-1" />Unknown</Badge>;
    }
  };

  const getDBTypeBadge = (dbType) => {
    const colors = {
      mysql: 'bg-blue-50 text-blue-700 border-blue-200',
      mariadb: 'bg-teal-50 text-teal-700 border-teal-200',
      mssql: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    const labels = { mysql: 'MySQL', mariadb: 'MariaDB', mssql: 'MS SQL' };
    return <Badge className={`${colors[dbType] || 'badge-neutral'} border`}>{labels[dbType] || dbType}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="connections-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold text-slate-900">Connections</h1>
        <Button 
          className="bg-teal-700 hover:bg-teal-600"
          onClick={() => openDialog(activeTab)}
          data-testid="add-connection-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add {activeTab === 'tally' ? 'Tally' : 'Database'} Connection
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="tally" className="gap-2" data-testid="tally-tab">
            <Server className="h-4 w-4" />
            Tally Connections ({tallyConnections.length})
          </TabsTrigger>
          <TabsTrigger value="db" className="gap-2" data-testid="db-tab">
            <Database className="h-4 w-4" />
            Database Connections ({dbConnections.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tally">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="table-cell">Name</TableHead>
                    <TableHead className="table-cell">IP/URL</TableHead>
                    <TableHead className="table-cell">Port</TableHead>
                    <TableHead className="table-cell">Company</TableHead>
                    <TableHead className="table-cell">Status</TableHead>
                    <TableHead className="table-cell">Last Tested</TableHead>
                    <TableHead className="table-cell text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tallyConnections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        <Server className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No Tally connections configured</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tallyConnections.map((conn) => (
                      <TableRow key={conn.id} className="table-row" data-testid={`tally-row-${conn.id}`}>
                        <TableCell className="table-cell font-medium">{conn.name}</TableCell>
                        <TableCell className="table-cell font-mono text-sm">{conn.ip_url}</TableCell>
                        <TableCell className="table-cell">{conn.port}</TableCell>
                        <TableCell className="table-cell">{conn.company_name || '-'}</TableCell>
                        <TableCell className="table-cell">{getStatusBadge(conn.status)}</TableCell>
                        <TableCell className="table-cell text-sm text-slate-500">
                          {conn.last_tested ? format(parseISO(conn.last_tested), 'MMM d, HH:mm') : '-'}
                        </TableCell>
                        <TableCell className="table-cell text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleTestConnection('tally', conn.id)} disabled={testingId === conn.id}>
                              {testingId === conn.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openDialog('tally', conn)}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openDeleteDialog('tally', conn)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="db">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="table-cell">Name</TableHead>
                    <TableHead className="table-cell">Type</TableHead>
                    <TableHead className="table-cell">Host</TableHead>
                    <TableHead className="table-cell">Port</TableHead>
                    <TableHead className="table-cell">Database</TableHead>
                    <TableHead className="table-cell">Status</TableHead>
                    <TableHead className="table-cell">Last Tested</TableHead>
                    <TableHead className="table-cell text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dbConnections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No database connections configured</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    dbConnections.map((conn) => (
                      <TableRow key={conn.id} className="table-row" data-testid={`db-row-${conn.id}`}>
                        <TableCell className="table-cell font-medium">{conn.name}</TableCell>
                        <TableCell className="table-cell">{getDBTypeBadge(conn.db_type)}</TableCell>
                        <TableCell className="table-cell font-mono text-sm">{conn.host}</TableCell>
                        <TableCell className="table-cell">{conn.port}</TableCell>
                        <TableCell className="table-cell">{conn.database}</TableCell>
                        <TableCell className="table-cell">{getStatusBadge(conn.status)}</TableCell>
                        <TableCell className="table-cell text-sm text-slate-500">
                          {conn.last_tested ? format(parseISO(conn.last_tested), 'MMM d, HH:mm') : '-'}
                        </TableCell>
                        <TableCell className="table-cell text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleTestConnection('db', conn.id)} disabled={testingId === conn.id}>
                              {testingId === conn.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openDialog('db', conn)}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openDeleteDialog('db', conn)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingConnection ? 'Edit' : 'Add'} {dialogType === 'tally' ? 'Tally' : 'Database'} Connection
            </DialogTitle>
            <DialogDescription>
              {dialogType === 'tally' ? 'Configure your Tally server connection' : 'Configure your database connection'}
            </DialogDescription>
          </DialogHeader>
          
          {dialogType === 'tally' ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Connection Name *</Label>
                <Input value={tallyForm.name} onChange={(e) => setTallyForm({ ...tallyForm, name: e.target.value })} placeholder="Production Tally" data-testid="tally-name-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label>IP / URL *</Label>
                  <Input value={tallyForm.ip_url} onChange={(e) => setTallyForm({ ...tallyForm, ip_url: e.target.value })} placeholder="192.168.1.100" data-testid="tally-ip-input" />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input type="number" value={tallyForm.port} onChange={(e) => setTallyForm({ ...tallyForm, port: parseInt(e.target.value) || 9000 })} data-testid="tally-port-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Company Name (Optional)</Label>
                <Input value={tallyForm.company_name} onChange={(e) => setTallyForm({ ...tallyForm, company_name: e.target.value })} placeholder="My Company Ltd" />
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Connection Name *</Label>
                <Input value={dbForm.name} onChange={(e) => setDbForm({ ...dbForm, name: e.target.value })} placeholder="Production DB" data-testid="db-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Database Type *</Label>
                <Select value={dbForm.db_type} onValueChange={(value) => {
                  const type = dbTypes.find(t => t.value === value);
                  setDbForm({ ...dbForm, db_type: value, port: type?.defaultPort || 3306 });
                }}>
                  <SelectTrigger data-testid="db-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {dbTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label>Host *</Label>
                  <Input value={dbForm.host} onChange={(e) => setDbForm({ ...dbForm, host: e.target.value })} placeholder="localhost" data-testid="db-host-input" />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input type="number" value={dbForm.port} onChange={(e) => setDbForm({ ...dbForm, port: parseInt(e.target.value) || 3306 })} data-testid="db-port-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Database *</Label>
                <Input value={dbForm.database} onChange={(e) => setDbForm({ ...dbForm, database: e.target.value })} placeholder="mydb" data-testid="db-database-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Username *</Label>
                  <Input value={dbForm.username} onChange={(e) => setDbForm({ ...dbForm, username: e.target.value })} placeholder="root" data-testid="db-username-input" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={dbForm.password} onChange={(e) => setDbForm({ ...dbForm, password: e.target.value })} placeholder="••••••••" data-testid="db-password-input" />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-600" onClick={dialogType === 'tally' ? handleSaveTally : handleSaveDB} data-testid="save-connection-btn">
              {editingConnection ? 'Update' : 'Create'} Connection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Connection</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{deletingConnection?.name}"?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectionsPage;
